import React from 'react';
import { Alert, Icon, IconButton } from '@kontentino/ui';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons/faPenToSquare';
import { useTranslation } from 'react-i18next';
import type { InstagramAudioSelection } from 'types/Attachments';
import InstagramAudioArtwork from './InstagramAudioArtwork';
import InstagramAudioDisabledTooltip from './InstagramAudioDisabledTooltip';
import InstagramAudioConnectProfileNotice from './InstagramAudioConnectProfileNotice';
import InstagramAudioVolumeControls from './InstagramAudioVolumeControls';
import InstagramAudioMixPreview from './InstagramAudioMixPreview';
import { formatTrackDetails } from '../utils/formatTrackDetails';
import { resolvePreviewSrc } from '../utils/resolvePreviewSrc';
import {
  getInstagramAudioErrorCode,
  isProfileConnectionError,
  isTrackGone,
} from '../utils/instagramAudioError';
import { useInstagramAudioTrackQuery } from '../hooks/useInstagramAudioTrackQuery';
import { useVolumeDraft } from '../hooks/useVolumeDraft';

interface InstagramAudioSummaryProps {
  value: InstagramAudioSelection;
  pageId: number;
  videoSrc: string | null;
  videoDurationMs: number | null;
  isDisabled?: boolean;
  disabledReason?: string | null;
  isLibraryAvailable?: boolean;
  canPromptConnection: boolean;
  onChange: (value: InstagramAudioSelection) => void;
  onRemove: () => void;
  onOpenPicker: () => void;
}

const InstagramAudioSummary = ({
  value,
  pageId,
  videoSrc,
  videoDurationMs,
  isDisabled,
  disabledReason,
  isLibraryAvailable = true,
  canPromptConnection,
  onChange,
  onRemove,
  onOpenPicker,
}: InstagramAudioSummaryProps) => {
  const { t } = useTranslation();

  const { draft, onDrag, onCommit } = useVolumeDraft(
    { audioVolume: value.audio_volume, videoVolume: value.video_volume },
    ({ audioVolume, videoVolume }) =>
      onChange({
        ...value,
        audio_volume: audioVolume,
        video_volume: videoVolume,
      }),
  );

  const trackQuery = useInstagramAudioTrackQuery({
    pageId,
    audioId: value.audio_id,
    enabled: isLibraryAvailable,
  });

  const previewSrc = resolvePreviewSrc(trackQuery.data ?? null);
  const errorCode = getInstagramAudioErrorCode(trackQuery.error);
  const needsConnection =
    canPromptConnection && isProfileConnectionError(errorCode);
  const isUnavailable = !needsConnection && isTrackGone(trackQuery.error);

  return (
    <div
      className="tw-flex tw-flex-col tw-gap-4 tw-rounded-lg tw-border tw-border-grayscale-10 tw-p-3"
      data-name="instagram-audio_summary"
    >
      <div className="tw-flex tw-items-center tw-gap-3">
        <InstagramAudioArtwork
          src={value.cover_artwork_thumbnail_uri ?? value.profile_picture_url}
          alt={value.title ?? ''}
          size="md"
        />

        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
          <span className="tw-truncate tw-text-sm tw-font-medium tw-text-grayscale-180">
            {value.title}
          </span>
          <span className="tw-truncate tw-text-xs tw-text-grayscale-140">
            {formatTrackDetails(value)}
          </span>
        </div>

        <InstagramAudioDisabledTooltip
          reason={isDisabled ? disabledReason : null}
        >
          <IconButton
            variant="tertiary"
            size="small"
            icon={<Icon icon={faPenToSquare} size="sm" />}
            disabled={isDisabled || !isLibraryAvailable}
            onClick={onOpenPicker}
            aria-label={t('instagramAudio.changeAudio')}
            data-cy="instagram-audio_change-button"
          />
        </InstagramAudioDisabledTooltip>
        <InstagramAudioDisabledTooltip
          reason={isDisabled ? disabledReason : null}
        >
          <IconButton
            variant="tertiary"
            size="small"
            icon={<Icon icon={faXmark} size="sm" />}
            disabled={isDisabled}
            onClick={onRemove}
            aria-label={t('instagramAudio.removeAudio')}
            data-cy="instagram-audio_remove-button"
          />
        </InstagramAudioDisabledTooltip>
      </div>

      {needsConnection && (
        <InstagramAudioConnectProfileNotice
          pageId={pageId}
          errorCode={errorCode}
        />
      )}

      {isUnavailable && (
        <Alert variant="danger">{t('instagramAudio.trackUnavailable')}</Alert>
      )}

      <InstagramAudioMixPreview
        videoSrc={videoSrc}
        audioSrc={previewSrc}
        audioVolume={draft.audioVolume}
        videoVolume={draft.videoVolume}
        audioDurationMs={value.duration_in_ms}
        videoDurationMs={videoDurationMs}
      />

      <InstagramAudioVolumeControls
        volumes={draft}
        hasVideo={!!videoSrc}
        isDisabled={isDisabled}
        onDrag={onDrag}
        onCommit={onCommit}
      />
    </div>
  );
};

export default InstagramAudioSummary;
